import {
  Form as AntdForm,
  Input,
  InputNumber,
  notification,
  Radio,
  Select,
} from "antd"
import Upload from "antd/es/upload"
import { NotFound } from "components/molecules/404"
import React, { useEffect, useMemo, useState } from "react"
import { Link, useNavigate, useParams } from "react-router-dom"
import { useRecoilState, useRecoilValue } from "recoil"
import {
  isClientNotFoundState,
  isCompanyNotFoundState,
} from "recoils/isNotFoundState"
import styled from "styled-components"

import { Button } from "../../../components/atoms/Button"
import { Spin } from "../../../components/atoms/Spin"
import {
  Form,
  FormItem,
  FormItemCenter,
  FormTitle,
} from "../../../components/atoms/form"
import {
  JobTypes,
  Prefectures,
  RequiredQualifications,
  ShipTypes,
} from "../../../components/const"
import { useInitializeAccountData } from "../../../hooks/useInitializeAccountData"
import { useAsyncLoading } from "../../../hooks/useLoading"
import { validIncludeUrl } from "../../../lib"
import { convertDateToString } from "../../../lib/date"
import { companyState } from "../../../recoils/companyState"
import { recruitsState } from "../../../recoils/recruitsState"
import { postRecruit } from "../../../usecases/firebase/functions"
import { uploadStorage } from "../../../usecases/firebase/storage"

import type { Recruit } from "../../../interfaces/RecruitIF"
import type { UploadFile } from "antd/es/upload/interface"

const { success } = notification

type RecruitEditPageType = "create" | "edit"

const { Option } = Select

const getShipTypeDetailOptions = (shipType: string): string[] => {
  switch (shipType) {
    case "作業船":
      return ["曳船 (兼押船)", "19t 曳船 (兼押船)", "チャッカー船"]
    case "タンカー":
      return [
        "油槽船",
        "アスファルト船",
        "ケミカル船",
        "LNG船",
        "バンカー(給油船)",
      ]
    case "貨物船":
      return [
        "コンテナ船",
        "カーゴ (一般貨物船)",
        "バラ積み船",
        "セメント船",
        "石灰石専用船",
        "鉱炭専用船",
        "自動車運搬船",
        "ガット船",
        "RORO船",
      ]
    case "フェリー":
      return ["フェリー", "大型カーフェリー", "ジェットフォイル", "旅客観光船"]
    case "特殊船":
      return ["調査観測船", "産業廃棄物運搬船"]
    default:
      return []
  }
}

const yearsOnBoards: number[] = []
for (let i = 1; i <= 30; i++) {
  yearsOnBoards.push(i)
}

const getOptions = (values: string[]) => {
  return values.map((value) => {
    return <Option key={value}>{value}</Option>
  })
}

type PlanAttentionProps = {
  label: string
  isImage?: boolean
}

const PlanAttention = (props: PlanAttentionProps): JSX.Element => {
  const planLabel = useMemo(() => {
    if (props.isImage) {
      return (
        <>
          {props.label}は<Link to="/plan">有料プランA</Link>
          会員様は2枚まで、<Link to="/plan">有料プランG</Link>
          会員様は3枚まで設定できます。
        </>
      )
    }
    return (
      <>
        {props.label}は<Link to="/plan">有料プランG</Link>
        会員様のみ設定できます。
      </>
    )
  }, [props])

  return <AttentionText>{planLabel}</AttentionText>
}

const imageName = (uid: string): string => {
  return `${new Date().getTime()}-${uid}`
}

export const RecruitEditPage = (): JSX.Element => {
  const params = useParams()
  const [form] = AntdForm.useForm()
  const [isLoading, setIsLoading] = useState(false)
  const [loading, asyncLoading] = useAsyncLoading("recruitEdit")
  const [pageType, setPageType] = useState<RecruitEditPageType>("create")
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [recruits, setRecruits] = useRecoilState(recruitsState)
  const company = useRecoilValue(companyState)
  const isClientNotFound = useRecoilValue(isClientNotFoundState)
  const isCompanyNotFound = useRecoilValue(isCompanyNotFoundState)
  const initialize = useInitializeAccountData()
  const navigate = useNavigate()
  const [shipType, setShipType] = useState<string>("")

  const recruitId = params.id

  useEffect(() => {
    asyncLoading(initialize().catch((e) => console.warn(e)))
  }, [])

  useEffect(() => {
    setPageType(recruitId === undefined ? "create" : "edit")
    const currentRecruit = recruits.find((r) => r.recruitId === recruitId)
    if (!currentRecruit) return

    form.setFieldsValue({
      ...currentRecruit,
      videoUrl: currentRecruit.videos ? currentRecruit.videos[0] : "",
      employmentStatus: currentRecruit.employmentStatus.toString(),
      recruitStartedAt: convertDateToString(currentRecruit.recruitStartedAt),
      workStartedAt: convertDateToString(currentRecruit.workStartedAt),
      isPrivate: currentRecruit.isPrivate
        ? currentRecruit.isPrivate.toString()
        : "false",
    })

    setShipType(currentRecruit.shipType)

    const files = currentRecruit.images.map((image) => {
      return {
        uid: image,
        url: image,
        status: "done",
      } as UploadFile
    })
    setFileList(files)
  }, [recruitId, loading])

  const onFinish = async () => {
    try {
      setIsLoading(true)
      const recruitParams = form.getFieldsValue()
      const res = await postRecruit({
        id: recruitId ?? undefined,
        recruit: {
          ...recruitParams,
          videos: recruitParams.videoUrl ? [recruitParams.videoUrl] : [],
          isPrivate: recruitParams.isPrivate === "true",
          images: fileList
            .filter((file) => file.status === "done")
            .map((file) => file.url),
        },
      })
      const newRecruit = res.data as Recruit
      if (pageType === "create") {
        setRecruits([...recruits, newRecruit])
      } else {
        const updatedRecruits = recruits.map((recruit) => {
          if (recruit.recruitId === newRecruit.recruitId) {
            return newRecruit
          }
          return recruit
        })
        setRecruits(updatedRecruits)
      }
      setIsLoading(false)
      navigate(`/recruit/${newRecruit.recruitId}/show`)
      success({ message: "求人情報を保存しました。" })
    } catch (e: any) {
      console.warn(e)
      setIsLoading(false)
      notification.error({ message: "予期せぬエラーが発生しました" })
    }
  }

  const onFinishFailed = (errorInfo: any) => {
    notification.error({ message: "予期せぬエラーが発生しました" })
    console.log("Failed:", errorInfo)
  }

  const Title = useMemo(() => {
    if (pageType === "create") {
      return <FormTitle>求人新規作成</FormTitle>
    }

    return <FormTitle>求人編集</FormTitle>
  }, [pageType])

  const disableContact = useMemo((): boolean => {
    if (!company?.plan) {
      return true
    }

    return company.plan < 2
  }, [company])

  const disableImage = useMemo((): boolean => {
    if (!company?.plan) {
      return true
    }

    return company.plan < 1
  }, [company])

  const imageMaxCount = useMemo((): number => {
    if (!company?.plan) {
      return 0
    }

    if (company.plan === 1) {
      return 2
    }

    return 3
  }, [company])

  const ShipTypeDetail = useMemo(() => {
    if (["漁船", "ガット船", "その他船舶"].includes(shipType)) {
      return (
        <FormItem key={shipType} label="船種(小項目)" name="shipTypeDetail">
          <Input
            placeholder="船種(小項目)"
            maxLength={100}
            disabled={!shipType}
          />
        </FormItem>
      )
    }

    return (
      <FormItem
        key={shipType}
        label="船種(小項目)"
        name="shipTypeDetail"
        required
        rules={[{ required: true, message: "船種(小項目)を入力してください" }]}
      >
        <Select showSearch placeholder="船種(小項目)">
          {getOptions(getShipTypeDetailOptions(shipType))}
        </Select>
      </FormItem>
    )
  }, [shipType])

  const YearsOnBordOptions = yearsOnBoards.map((yearsOnBord) => {
    if (yearsOnBord === 30) {
      return <Option key="30年以上">30年以上</Option>
    }

    const year = `${yearsOnBord.toString()}年`
    return <Option key={year}>{year}</Option>
  })

  const addImage = (uid: string, downloadUrl: string) => {
    const newFile = {
      uid: uid,
      url: downloadUrl,
      status: "done",
    } as UploadFile

    setFileList([...fileList, newFile])
  }

  const onRemove = (file: UploadFile) => {
    const selectedFiles = fileList.filter(
      (f: UploadFile) => f.uid !== file.uid && f.status === "done",
    )

    setFileList([...selectedFiles])
  }

  const customUpload = async (options: any) => {
    const { file, onSuccess } = options

    const extension = file.type.substring(6)

    uploadStorage({
      file: file,
      path: `/recruits/${imageName(file.uid)}.${extension}`,
      onUpload: (downloadUrl: string) => {
        onSuccess(downloadUrl)
        addImage(file.uid, downloadUrl)
      },
    })
  }

  if (isClientNotFound || isCompanyNotFound) {
    return <NotFound />
  }

  return loading ? (
    <Spin />
  ) : (
    <Form
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      requiredMark={false}
      form={form}
    >
      {Title}

      <FormItem
        label="求人タイトル"
        name="title"
        required
        rules={[{ required: true, message: "求人タイトルを入力してください" }]}
      >
        <Input placeholder="〇〇に関する求人" maxLength={100} />
      </FormItem>

      <FormItem
        label="求人詳細"
        name="description"
        required
        rules={[
          { required: true, message: "求人詳細を入力してください" },
          {
            validator: validIncludeUrl,
          },
        ]}
      >
        <Input.TextArea
          rows={5}
          placeholder="求人詳細を入力してください。"
          maxLength={500}
        />
      </FormItem>
      <HintText>
        ホームページ URL やメールアドレスは記載されないようにお願いいたします。
        <br />
        掲載されている場合、運営の方で URL を削除する場合があります。
      </HintText>

      <FormItem label="募集開始日" name="recruitStartedAt">
        <Input type="date" placeholder="2022-09-01" />
      </FormItem>

      <FormItem
        label="航行区域"
        name="navigationArea"
        required
        rules={[
          { required: true, message: "航行区域を入力してください" },
          {
            validator: validIncludeUrl,
          },
        ]}
      >
        <Input.TextArea
          rows={5}
          placeholder="鹿児島県、沖縄県"
          maxLength={500}
        />
      </FormItem>

      <FormItem label="内航/外航" name="seaRoute">
        <Radio.Group>
          <Radio value="内航">内航</Radio>
          <Radio value="外航">外航</Radio>
        </Radio.Group>
      </FormItem>

      <FormItem
        label="船種(大項目)"
        name="shipType"
        required
        rules={[{ required: true, message: "船種を入力してください" }]}
      >
        <Select
          showSearch
          placeholder="船種(大項目)"
          onSelect={(value: string) => {
            form.resetFields(["shipTypeDetail"])
            setShipType("")
            setTimeout(() => {
              setShipType(value)
            }, 0)
          }}
        >
          {getOptions(ShipTypes)}
        </Select>
      </FormItem>

      {ShipTypeDetail}

      <FormItem
        label="職種"
        name="jobType"
        required
        rules={[{ required: true, message: "職種を入力してください" }]}
      >
        <Select showSearch placeholder="職種">
          {getOptions(JobTypes)}
        </Select>
      </FormItem>

      <FormItem
        label="乗組員数"
        name="crewNumber"
        rules={[
          {
            type: "number",
            message: "乗組員数を数字で入力してください",
          },
        ]}
      >
        <InputNumber placeholder="3" addonAfter="人" maxLength={100} min={0} />
      </FormItem>

      <FormItem
        label="募集人数"
        name="recruitmentCount"
        rules={[
          {
            type: "number",
            message: "募集人数を数字で入力してください",
          },
        ]}
      >
        <InputNumber placeholder="3" addonAfter="人" maxLength={100} min={0} />
      </FormItem>

      <FormItem
        label="勤務地"
        name="workLocation"
        required
        rules={[{ required: true, message: "勤務地を入力してください" }]}
      >
        <Select showSearch placeholder="勤務地">
          {getOptions(Prefectures)}
        </Select>
      </FormItem>

      <FormItem
        label="勤務開始日"
        name="workStartedAt"
        required
        rules={[{ required: true, message: "勤務開始日を入力してください" }]}
      >
        <Input type="date" placeholder="2022-09-01" />
      </FormItem>

      <FormItem label="乗船履歴" name="yearsOnBoard">
        <Select showSearch placeholder="乗船履歴">
          {YearsOnBordOptions}
        </Select>
      </FormItem>

      <FormItem
        label="必要な資格"
        name="requiredQualifications"
        required
        rules={[{ required: true, message: "必要な資格を入力してください" }]}
      >
        <Select showSearch placeholder="必要な資格">
          {getOptions(RequiredQualifications)}
        </Select>
      </FormItem>

      <FormItem
        label="雇用期間"
        name="employmentTerm"
        required
        rules={[{ required: true, message: "雇用期間を入力してください" }]}
      >
        <Input placeholder="3ヶ月" maxLength={100} />
      </FormItem>

      <FormItem label="賃金手取月額" name="monthlySalary">
        <Input placeholder="賃金手取月額" maxLength={100} />
      </FormItem>

      <FormItem label="乗下船時給料の変動" name="salaryChangeable">
        <Radio.Group>
          <Radio value="有り">有り</Radio>
          <Radio value="無し">無し</Radio>
        </Radio.Group>
      </FormItem>

      <FormItem
        label="雇用形態"
        name="employmentStatus"
        required
        rules={[{ required: true, message: "雇用形態を入力してください" }]}
      >
        <Radio.Group>
          <Radio value="1">短期雇用</Radio>
          <Radio value="2">体験就職</Radio>
          <Radio value="3">常用雇用（試用期間後）</Radio>
        </Radio.Group>
      </FormItem>

      <FormItem label="会社の電話番号" name="telephoneNumber">
        <Input type="tel" placeholder="090-0000-0000" maxLength={100} />
      </FormItem>

      <FormItem label="担当者の電話番号" name="contactTelephoneNumber">
        <Input type="tel" placeholder="090-0000-0000" maxLength={100} />
      </FormItem>

      <FormItem
        label="ホームページURL"
        name="website"
        rules={[
          {
            type: "url",
            message: "ホームページURLを入力してください",
          },
        ]}
        help={<PlanAttention label="ホームページURL" />}
      >
        <Input
          placeholder="https://www.example.com/"
          maxLength={500}
          disabled={disableContact}
        />
      </FormItem>

      <FormItem
        label="メールアドレス"
        name="email"
        rules={[
          {
            type: "email",
            message: "メールアドレスを入力してください",
          },
        ]}
        help={<PlanAttention label="メールアドレス" />}
      >
        <Input
          type="email"
          placeholder="recruit@example.com"
          maxLength={500}
          disabled={disableContact}
        />
      </FormItem>

      <FormItem
        label="求人イメージ画像"
        name="images"
        help={<PlanAttention label="求人イメージ画像" isImage={true} />}
      >
        <Upload
          accept="image/*"
          listType="picture-card"
          fileList={fileList}
          customRequest={customUpload}
          onRemove={onRemove}
          disabled={disableImage}
        >
          {fileList.length < imageMaxCount && "+ Upload"}
        </Upload>
      </FormItem>

      <FormItem
        label="求人イメージ動画(YouTube・TikTok動画)"
        name="videoUrl"
        rules={[
          {
            type: "url",
            message: "求人イメージ動画",
          },
        ]}
        help={<PlanAttention label="求人イメージ動画" />}
      >
        <Input
          placeholder="https://youtube.example.com/"
          maxLength={500}
          disabled={disableContact}
        />
      </FormItem>

      <FormItem
        label="求人の非掲載"
        name="isPrivate"
        help={<PlanAttention label="求人の非掲載設定" />}
      >
        <Radio.Group>
          <Radio value="false" disabled={disableContact}>
            掲載する
          </Radio>
          <Radio value="true" disabled={disableContact}>
            非掲載にする
          </Radio>
        </Radio.Group>
      </FormItem>

      <PrivateAttention>
        求人の非掲載を選択すると、アプリには掲載されなくなります。
        <br />
        求職者には気になるアピールをすることで、求人を表示できるようになります。
      </PrivateAttention>

      <FormItemCenter>
        <SaveButton
          isLoading={loading}
          className={"primary"}
          htmlType="submit"
          loading={isLoading}
        >
          保存
        </SaveButton>
      </FormItemCenter>
    </Form>
  )
}

const SaveButton = styled(Button)`
  width: 12rem;
`

const AttentionText = styled.p`
  margin: 0 0 10px;
`

const HintText = styled.p`
  color: rgba(0, 0, 0, 0.45);
  margin: -20px 0 24px;
`

const PrivateAttention = styled.p`
  color: rgba(0, 0, 0, 0.45);
  margin: 0 0 10px;
`
